import * as preact from "preact";
import * as _ from "underscore";

import * as practiceUtils from "practiceUtils";
import { ChallengeProps } from "practiceUtils";
import * as languageMap from "shared/languageMap";
import MultipleChoiceButtonRow, {
  MultipleChoiceButtonRowPersistedState,
} from "views/MultipleChoiceButtonRow";
import PracticeContextView from "views/PracticeContextView";
import PracticePlayTTSView from "views/PracticePlayTTSView";
import TransliterationView from "./TransliterationView";

interface State {
  distractors: string[];
  reportedRecallEase: boolean;
}

interface PersistedState {
  distractors: string[];
  reportedRecallEase: boolean;
  buttonRowState?: MultipleChoiceButtonRowPersistedState;
}

class PassiveMultipleChoiceChallengeView extends preact.Component<
  ChallengeProps,
  State
> {
  buttonRow: MultipleChoiceButtonRow | null = null;
  initialButtonRowState?: MultipleChoiceButtonRowPersistedState;

  constructor(props: ChallengeProps) {
    super(props);

    const persisted = props.persistedState as PersistedState | undefined;

    if (persisted) {
      this.state = {
        distractors: persisted.distractors,
        reportedRecallEase: persisted.reportedRecallEase,
      };
      this.initialButtonRowState = persisted.buttonRowState;
      return;
    }

    this.state = {
      distractors: _.sample(
        props.allUserWords
          .filter(
            (potentialDistractor) =>
              potentialDistractor.id !== props.userWord.id &&
              practiceUtils.normalize(potentialDistractor.state.value.word) !==
                practiceUtils.normalize(props.userWord.state.value.word) &&
              practiceUtils.normalize(
                potentialDistractor.state.value.translation
              ) !==
                practiceUtils.normalize(props.userWord.state.value.translation)
          )
          .map((userWord) =>
            [
              userWord.state.value.translation,
              ...userWord.state.value.alternativeTranslations,
            ].join(" / ")
          ),
        3
      ),
      reportedRecallEase: false,
    };
  }

  getPersistedState(): PersistedState {
    return {
      distractors: this.state.distractors,
      reportedRecallEase: this.state.reportedRecallEase,
      buttonRowState: this.buttonRow?.getPersistedState(),
    };
  }

  render() {
    const { userWord } = this.props;
    // Computed during render (not cached in state) so it reflects edits to the
    // word's context made mid-session, e.g. via the Edit Card modal.
    const clozeSentence = practiceUtils.getClozeSentence(
      userWord,
      this.props.selectedContextIndex
    );

    return (
      <div class="passiveMultipleChoiceChallenge">
        <h2>
          What does{" "}
          <PracticePlayTTSView
            text={userWord.state.value.word}
            language={userWord.state.value.language}
          />{" "}
          <span class="transliterationContainer">
            <TransliterationView
              language={userWord.state.value.language}
              text={userWord.state.value.word}
              concealed={!this.state.reportedRecallEase && !this.props.skipped}
            />
            "{userWord.state.value.word}"
          </span>{" "}
          mean here?
        </h2>
        <p>
          <PracticeContextView
            clozeSentence={clozeSentence}
            rtl={
              languageMap.map[userWord.state.value.language]?.direction ===
              "RTL"
            }
            mode={{ type: "complete" }}
          />
        </p>
        <MultipleChoiceButtonRow
          ref={(ref) => (this.buttonRow = ref)}
          initialState={this.initialButtonRowState}
          correctAnswer={[
            userWord.state.value.translation,
            ...userWord.state.value.alternativeTranslations,
          ].join(" / ")}
          distractors={this.state.distractors}
          onSuccess={() => {
            this.props.completedCallback({
              type: "show-feedback",
              correct: "yes",
            });
            if (!this.state.reportedRecallEase) {
              this.props.reportRecallEase(5, 0.4);
              this.setState({ reportedRecallEase: true });
            }
          }}
          onFail={() => {
            this.props.completedCallback({
              type: "show-feedback",
              correct: "no",
              message: {
                title: "Incorrect",
                body: `The correct answer is "${userWord.state.value.translation}"`,
              },
            });
            if (!this.state.reportedRecallEase) {
              this.props.reportRecallEase(0, 0.4);
              this.setState({ reportedRecallEase: true });
            }
          }}
          showCorrectAnswer={this.props.skipped}
        />
      </div>
    );
  }
}

export default PassiveMultipleChoiceChallengeView;
